import { API } from '../Api';
import { PUBLIC_API_ENDPOINT } from '../../constants/platform';
import { AdAccount } from '../ads/ad-account.api';

// "Hợp tác quảng cáo" (Brand Deal) — doanh nghiệp (AdAccount Verified) tạo chiến dịch, mời creator
// đăng bài theo đúng nội dung mẫu (ký quỹ từ ví doanh nghiệp), admin duyệt chiến dịch + xử lý deal
// "Chờ admin xem xét". `auid` (chủ thể gọi) gateway tự gán từ JWT — KHÔNG truyền từ FE.
//
// Route theo payment branch feat/brand-deal:
// - Creator: api/v1/brand-deals/*            -> /v1/payment-service/brand-deals/*
// - Doanh nghiệp: api/v1/brand-campaigns/*   -> /v1/payment-service/brand-campaigns/*
// - Admin: api/v1/admin/brand-deals/*        -> /v1/payment-service/admin/brand-deals/*

export type BrandDealStatus =
  | 'Invited'
  | 'Publishing'
  | 'Live'
  | 'Completed'
  | 'Declined'
  | 'Expired'
  | 'Cancelled'
  | 'Forfeited'
  | 'PublishFailed'
  | 'NeedsReview'
  | 'AwaitingPublish';

export type BrandCampaignStatus = 'Draft' | 'PendingReview' | 'Approved' | 'Rejected' | 'Closed';

// M2 — sàn mở cho creator xin tham gia (payment branch feat/brand-market).
export type BrandListingStatus = 'None' | 'Open' | 'Closed';
export type BrandApplicationStatus = 'Pending' | 'Approved' | 'Rejected' | 'Withdrawn' | 'Expired' | 'Closed';

/** Loại người đặt đơn (spec 2026-10-09 §2.4). Thiếu trường (máy chủ cũ) = doanh nghiệp. */
export type OrdererKind = 'Business' | 'Individual';

/** Định dạng nội dung của chiến dịch (Video | Ảnh | Bài viết). Null = chiến dịch tạo trước bản này, không ràng buộc. */
export type BrandContentFormat = 'Video' | 'Image' | 'Article';

export interface MediaRef {
  id: string;
  url: string;
}

// ─────────── Kháng cáo bản quyền (M4, spec 2026-10-05-brand-dispute) ───────────

export type BrandDisputeStatus = 'Open' | 'Upheld' | 'Dismissed';

/** Kháng cáo của 1 nhiệm vụ — dùng chung creator/doanh nghiệp/admin (BrandDisputeDto phía payment),
 * trường nào bên chưa gửi thì null (businessResponse/decisionReason…). */
export interface BrandDispute {
  id: string;
  dealId: string;
  status: BrandDisputeStatus;
  description: string;
  evidenceLinks: string[];
  evidenceImages: MediaRef[];
  businessResponse: string | null;
  businessResponseLinks: string[] | null;
  responseDueAt: string;
  businessRespondedAt: string | null;
  decisionReason: string | null;
  decidedAt: string | null;
  createdAt: string;
  readyForDecision: boolean;
  // ── Kết quả chia tiền (BrandDisputeDto phía payment) — null khi chưa quyết; tuỳ chọn để chạy được khi payment chưa deploy. ──
  creatorPercent?: number | null;
  /** Số Ca creator thực nhận sau phí; chỉ có ở chi tiết nhiệm vụ và kết quả vừa quyết. */
  creatorReceivedCa?: number | null;
  refundVnd?: number | null;
  /** Có khi người đặt là cá nhân và trọng tài chia tiền. */
  refundCa?: number | null;
  /** Chỉ có giá trị trên response của quyết kháng cáo (`adminDecideDispute`, upheld=true) — true nếu doanh
   * nghiệp đã bị khoá sau quyết định (kể cả đã khoá từ trước), false nếu khoá tự động thất bại (admin cần
   * khoá tay ở tab "Doanh nghiệp bị khoá"). undefined/null ở mọi response khác. */
}

// ─────────── Doanh nghiệp — chiến dịch ───────────

export interface BrandCampaign {
  ordererKind?: OrdererKind; ownerIdentityName?: string | null;
  /** Tỉ giá (đồng một Ca) chụp lúc tạo chiến dịch cá nhân; thiếu thì dùng tỉ giá hiện tại. */
  vndPerCa?: number;
  id: string;
  /** Tài khoản doanh nghiệp sở hữu chiến dịch — dùng để tra ĐÚNG ví (vd số dư trước khi mời),
   * KHÔNG đối chiếu qua `brandName` (không duy nhất trên AdAccount.BusinessName — review Task 7 round 1). */
  adAccountId: string;
  title: string;
  caption: string;
  destinationUrl: string;
  holdDays: number;
  images: MediaRef[];
  videos: MediaRef[];
  status: BrandCampaignStatus;
  rejectReason: string | null;
  brandName: string;
  createdAt: string;

  // ── M2: thông tin sàn (BrandCampaignDto, spec §2.3) ──
  listingStatus: BrandListingStatus;
  slotPriceVnd: number | null;
  slotsTotal: number | null;
  slotsUsed: number;
  minFollowers: number;
  categoryIds: string[];
  applyDeadline: string | null;

  // ── M3: brief sáng tạo (spec §2.1) ──
  /** "Direct" (mặc định) | "Creative" (brief sáng tạo) | "Proposal" (nhận đề xuất). */
  kind: 'Direct' | 'Creative' | 'Proposal' | 'Booking';
  briefText: string | null;
  submitDays: number | null;

  // ── Luật đơn brief sáng tạo (BrandCampaignDto phía payment) — tuỳ chọn để FE chạy được khi payment chưa deploy. ──
  /** Số lần người đặt được yêu cầu sửa, 0–3. */
  maxRevisions?: number;
  /** Số ngày duyệt mỗi lần nộp; null với brief cũ (48 giờ). */
  reviewDays?: number | null;
  /** Brief đã có người nhận (kể cả đã kết thúc) ⇒ không đổi được số lần sửa. */
  hasDeals?: boolean;
  contentFormat?: BrandContentFormat | null;

  // ── Đơn đặt quảng cáo (kind = 'Booking'): chỉ có ở màn admin duyệt. ──
  booking?: BrandBookingDto | null;

  // ── Sàn chiến dịch công khai: chọn lúc tạo (Public lên sàn sau khi admin duyệt, Private chỉ creator được mời). ──
  visibility?: BrandCampaignVisibility | null;
  /** Hạn nhận đơn (ngày) người đặt chọn lúc tạo chiến dịch công khai, dùng khi tự lên sàn. */
  listingApplyDays?: number | null;
  /** Chỉ có trong phản hồi tạo chiến dịch riêng tư: kết quả từng lời mời. */
  inviteResults?: BrandCampaignInviteResult[] | null;
}

export type BrandCampaignVisibility = 'Public' | 'Private';

export interface BrandCampaignInviteResult {
  creatorId: string;
  ok: boolean;
  dealId: string | null;
  error: string | null;
}

export interface BrandBookingDto {
  ordererKind?: OrdererKind;
  creatorName: string;
  businessName: string;
  destinationUrl: string;
  totalVnd: number;
  post: { caption: string; images: MediaRef[]; videos: MediaRef[]; holdDays: number; priceVnd: number } | null;
  live: {
    plan: 'Monthly' | 'Hourly';
    purchasedSeconds: number;
    validDays: number;
    slotName: string;
    slotShape: 'Corner' | 'BottomBar' | 'Vertical';
    cardLogo: MediaRef;
    cardTitle: string;
    cardLine: string | null;
    priceVnd: number;
  } | null;
}

// ─────────── M5: đánh giá 2 chiều và điểm uy tín (Task 3, spec §2.3) ───────────

/** 1 lượt chấm đã công bố — nhúng trong chi tiết deal/nhiệm vụ làm `myRating`/`theirRating`. */
export interface BrandRatingSummary {
  stars: number;
  comment: string | null;
  createdAt: string;
}

export interface BrandDealSummary {
  id: string;
  creatorId: string;
  priceVnd: number;
  status: BrandDealStatus;
  validClicks: number;
  postId: string | null;
  endsAt: string | null;

  // ── Nội dung theo từng lời mời (spec 2026-10-12 §5). Tuỳ chọn để FE chạy được khi payment chưa deploy. ──
  contentVersion?: number;
  existingPostId?: string | null;
  /** Bài có sẵn bị post-service từ chối lúc creator đồng ý, người đặt cần chọn lại. */
  existingPostUnavailable?: boolean;
  directEditCount?: number;
  directEditMax?: number;
  /** Lời mời đăng bài trực tiếp còn chờ creator trả lời ⇒ hiện nút "Sửa nội dung". */
  canEditContent?: boolean;

  // ── Lời mời làm brief (spec 2026-10-12 §7). Tuỳ chọn để FE chạy được khi payment chưa deploy. ──
  isBriefInvite?: boolean;
  /** Brief riêng của lời mời làm brief (null với đơn khác). */
  briefText?: string | null;
  briefVersion?: number;
  /** Còn chờ creator trả lời và chưa hết hạn ⇒ hiện nút "Sửa brief". */
  canEditBrief?: boolean;
  /** ISO có 'Z'; null = creator chưa nhận. */
  acceptedAt?: string | null;
  respondBy?: string;

  // ── M5: đánh giá 2 chiều — chỉ có ở chi tiết deal (spec §2.3). ──
  myRating?: BrandRatingSummary | null;
  /** Lượt chấm của phía bên kia — chỉ hiện khi ĐÃ CÔNG BỐ. */
  theirRating?: BrandRatingSummary | null;
  ratingDueAt?: string | null;
  /** Phòng trao đổi của creator này trong chiến dịch (spec 2026-10-12 §3.1). */
  threadId?: string | null;
}

export interface BrandCampaignDetail {
  campaign: BrandCampaign;
  totalHoldVnd: number;
  totalReleaseVnd: number;
  totalRefundVnd: number;
  totalFeeVnd: number;
  deals: BrandDealSummary[];

  // ── M2: tổng hợp sổ ký quỹ CẤP SÀN + số đơn chờ duyệt (BrandCampaignDetailDto, spec §2.3) ──
  listingHeldVnd: number;
  listingTransferredVnd: number;
  listingRefundedVnd: number;
  pendingApplications: number;
  /** Số đề xuất đang chờ (đơn nhận đề xuất) — tuỳ chọn để FE chạy được khi payment chưa deploy. */
  pendingProposals?: number;
  // Final review backend I-1: phần ký quỹ đã trả lại quỹ (vd đơn Creative bị từ chối, đóng sàn) — optional
  // vì backend cũ (trước fix) chưa trả field này, mặc định 0 khi thiếu.
  listingSlotReturnedVnd?: number;
}

export interface ClickDailyPoint {
  date: string;
  clicks: number;
}

// ─────────── Đo chuyển đổi (spec 2026-10-05-brand-conversion §2.3) ───────────

/** clicks/conversions/conversionRate — dùng ở chi tiết deal M1 (creator) và chi tiết nhiệm vụ M3
 * (creator); KHÔNG có orderId/value (creator không được xem, Review Focus 5). */
export interface BrandDealStats {
  clicks: number;
  conversions: number;
  conversionRate: number;
}

/** Chuyển đổi THỬ gần nhất (nút "Tạo lượt bấm thử") — null nếu chưa thử lần nào. */
export interface BrandConversionTest {
  at: string;
  orderId: string;
  value: number | null;
}

/** Trả về cho mục "Cài mã đo chuyển đổi" (doanh nghiệp, chủ chiến dịch). `secret` CHỈ có giá trị đúng 1
 * lần — lúc sinh khoá lần đầu (GET conversion-setup) hoặc tạo lại (rotate); mọi lần gọi khác chỉ có
 * `secretLast4`. */
export interface BrandConversionSetup {
  publicKey: string;
  secretLast4: string;
  secret?: string | null;
  scriptSnippet: string;
  serverExample: string;
  lastTest: BrandConversionTest | null;
}

export interface BrandConversionTestClickResult {
  url: string;
}

export interface BrandCampaignStatsTotals {
  clicks: number;
  conversions: number;
  conversionRate: number;
  valueVnd: number;
  browserCount: number;
  serverCount: number;
}

export interface BrandCampaignStatsByCreator {
  creatorId: string;
  displayName: string;
  clicks: number;
  conversions: number;
  conversionRate: number;
  valueVnd: number;
}

export interface BrandCampaignStatsByDay {
  date: string;
  clicks: number;
  conversions: number;
}

/** Thống kê hiệu quả chuyển đổi 1 chiến dịch (khung "Hiệu quả", doanh nghiệp). */
export interface BrandCampaignStats {
  totals: BrandCampaignStatsTotals;
  byCreator: BrandCampaignStatsByCreator[];
  byDay: BrandCampaignStatsByDay[];
}

export interface SaveBrandCampaignBody {
  adAccountId?: string | null;
  title: string;
  caption: string;
  destinationUrl: string;
  holdDays: number;
  images: MediaRef[];
  videos: MediaRef[];

  // ── M3: brief sáng tạo (Task 7) — chỉ gửi khi tạo/sửa brief Creative ──
  /** Không truyền = "Direct" (mặc định phía payment). Mô tả mong muốn của Proposal gửi ở `briefText`. */
  kind?: 'Direct' | 'Creative' | 'Proposal';
  briefText?: string;
  submitDays?: number;
  /** Số lần được yêu cầu sửa 0–3 (chỉ brief sáng tạo). Sửa mà không gửi thì payment giữ nguyên. */
  maxRevisions?: number;
  /** Bắt buộc khi tạo mới; sửa mà không gửi thì giữ nguyên. */
  contentFormat?: BrandContentFormat;

  // ── Chỉ khi tạo mới: Public kèm tham số sàn (tự lên sàn khi admin duyệt), Private kèm danh sách mời. ──
  visibility?: BrandCampaignVisibility;
  slotPriceVnd?: number;
  slotsTotal?: number;
  minFollowers?: number;
  categoryIds?: string[];
  listingApplyDays?: number;
  invites?: { creatorId: string; priceVnd: number }[];
}

const CAMPAIGNS_BASE = '/v1/payment-service/brand-campaigns';

export const getMyBrandCampaigns = () => API.get<BrandCampaign[]>(CAMPAIGNS_BASE).then((r) => r.data);

export const createBrandCampaign = (body: SaveBrandCampaignBody) =>
  API.post<BrandCampaign>(CAMPAIGNS_BASE, body).then((r) => r.data);

export const updateBrandCampaign = (campaignId: string, body: SaveBrandCampaignBody) =>
  API.put<BrandCampaign>(`${CAMPAIGNS_BASE}/${campaignId}`, body).then((r) => r.data);

export const submitBrandCampaign = (campaignId: string) =>
  API.post<BrandCampaign>(`${CAMPAIGNS_BASE}/${campaignId}/submit`).then((r) => r.data);

export const closeBrandCampaign = (campaignId: string) =>
  API.post<BrandCampaign>(`${CAMPAIGNS_BASE}/${campaignId}/close`).then((r) => r.data);

export const getBrandCampaignDetail = (campaignId: string) =>
  API.get<BrandCampaignDetail>(`${CAMPAIGNS_BASE}/${campaignId}`).then((r) => r.data);

export const getBrandCampaignClicks = (campaignId: string, days = 30) =>
  API.get<ClickDailyPoint[]>(`${CAMPAIGNS_BASE}/${campaignId}/clicks`, { params: { days } }).then(
    (r) => r.data
  );

// ─────────── Doanh nghiệp — "Cài mã đo chuyển đổi" + "Hiệu quả" (spec §2.3) ───────────

/** Lấy mục "Cài mã đo chuyển đổi" — sinh khoá lần đầu nếu chiến dịch chưa cài đặt (lần sinh đầu mới có
 * `secret` đầy đủ). */
export const getBrandConversionSetup = (campaignId: string) =>
  API.get<BrandConversionSetup>(`${CAMPAIGNS_BASE}/${campaignId}/conversion-setup`).then((r) => r.data);

/** "Tạo lại khoá" — khoá cũ (công khai + bí mật) mất hiệu lực ngay; trả `secret` đầy đủ 1 lần nữa. */
export const rotateBrandConversionKey = (campaignId: string) =>
  API.post<BrandConversionSetup>(`${CAMPAIGNS_BASE}/${campaignId}/conversion-setup/rotate`).then((r) => r.data);

/** "Tạo lượt bấm thử" — trả URL đích kèm mã lượt bấm thử để doanh nghiệp mở thử trong tab mới. */
export const createBrandConversionTestClick = (campaignId: string) =>
  API.post<BrandConversionTestClickResult>(`${CAMPAIGNS_BASE}/${campaignId}/conversion-setup/test-click`).then(
    (r) => r.data
  );

/** Khung "Hiệu quả" — `from`/`to` dạng `YYYY-MM-DD` (ngày UTC, bao gồm cả 2 đầu mút); bỏ trống = không
 * giới hạn. */
export const getBrandCampaignStats = (campaignId: string, from?: string, to?: string) =>
  API.get<BrandCampaignStats>(`${CAMPAIGNS_BASE}/${campaignId}/stats`, { params: { from, to } }).then(
    (r) => r.data
  );

// ─────────── Doanh nghiệp — danh bạ creator + mời ───────────

export interface BrandCreatorCard {
  userId: string;
  displayName: string;
  userName: string;
  avatar: string | null;
  followers: number;
  categories: string[];
  completedDeals: number;

  // ── M5: điểm uy tín creator — chỉ có ở danh bạ M1 (BrandCreativeHandlers dùng lại DTO này cho thẻ
  // creator phía nhiệm vụ M3, không gắn uy tín ⇒ undefined ở đó). ──
  ratingAverage?: number | null;
  ratingCount?: number;
  restrictedLarge?: boolean;

  // ── Bảng giá creator (null = không bán kiểu đó) và còn vị trí livestream đang bật hay không. ──
  postPriceVnd?: number | null;
  liveMonthlyPriceVnd?: number | null;
  liveMonthlyHours?: number | null;
  liveHourlyPriceVnd?: number | null;
  hasLiveSlots?: boolean;
}

export interface PaginationResponse<T> {
  totalItems: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  items: T[];
}

export const getBrandCreatorDirectory = (params: {
  q?: string;
  categoryId?: string;
  page?: number;
  adAccountId?: string;
  /** Chỉ creator đang bán quảng cáo livestream (có vị trí bật + có giá livestream). */
  liveOnly?: boolean;
}) =>
  API.get<PaginationResponse<BrandCreatorCard>>(`${CAMPAIGNS_BASE}/creators`, { params }).then(
    (r) => r.data
  );

export const inviteBrandCreator = (campaignId: string, creatorId: string, priceVnd: number, existingPostId?: string | null) =>
  API.post<BrandDealSummary>(`${CAMPAIGNS_BASE}/${campaignId}/invites`, {
    creatorId,
    priceVnd,
    existingPostId: existingPostId || undefined,
  }).then((r) => r.data);

export const cancelBrandDeal = (dealId: string) =>
  API.post<BrandDealSummary>(`${CAMPAIGNS_BASE}/deals/${dealId}/cancel`).then((r) => r.data);

// ─────────── M5: đánh giá 2 chiều và điểm uy tín — doanh nghiệp (Task 3, spec §2.3) ───────────

export const getBusinessPendingRatings = () =>
  API.get<BrandRatingPending[]>(`${CAMPAIGNS_BASE}/ratings/pending`).then((r) => r.data);

export const submitBusinessRating = (dealId: string, stars: number, comment?: string) =>
  API.post<SubmitBrandRatingResult>(`${CAMPAIGNS_BASE}/ratings/${dealId}`, { stars, comment: comment || undefined }).then(
    (r) => r.data
  );

// ─────────── Doanh nghiệp — sàn mở (M2) ───────────
// Dùng ở Task 4 (trang chi tiết chiến dịch, doanh nghiệp). Thêm ở đây để chỉ có 1 nơi khai route M2.

export interface OpenBrandListingBody {
  slotPriceVnd: number;
  slotsTotal: number;
  minFollowers: number;
  categoryIds: string[];
  applyDays: number;
}

export const openBrandListing = (campaignId: string, body: OpenBrandListingBody) =>
  API.post<BrandCampaignDetail>(`${CAMPAIGNS_BASE}/${campaignId}/listing`, body).then((r) => r.data);

export const closeBrandListing = (campaignId: string) =>
  API.post<BrandCampaignDetail>(`${CAMPAIGNS_BASE}/${campaignId}/listing/close`).then((r) => r.data);

/** 1 đơn xin tham gia — nhìn từ phía DOANH NGHIỆP (GET {id}/applications, duyệt/từ chối). */
export interface BrandApplicationForBusiness {
  id: string;
  creator: BrandCreatorCard;
  message: string | null;
  status: BrandApplicationStatus;
  rejectReason: string | null;
  createdAt: string;
  decidedAt: string | null;
  dealId: string | null;
  /** Phòng trao đổi của creator này trong chiến dịch (spec 2026-10-12 §3.1). */
  threadId?: string | null;
}

export const getCampaignApplications = (campaignId: string, status?: BrandApplicationStatus) =>
  API.get<BrandApplicationForBusiness[]>(`${CAMPAIGNS_BASE}/${campaignId}/applications`, {
    params: { status },
  }).then((r) => r.data);

export interface ApproveBrandApplicationResult {
  applicationId: string;
  dealId: string;
  status: BrandApplicationStatus;
  dealStatus: BrandDealStatus;
  postId: string | null;
  endsAt: string | null;
  message: string | null;
}

export const approveBrandApplication = (applicationId: string) =>
  API.post<ApproveBrandApplicationResult>(`${CAMPAIGNS_BASE}/applications/${applicationId}/approve`).then(
    (r) => r.data
  );

/** Từ chối đơn — lý do tuỳ chọn (≤200 ký tự). Thương lượng KHÔNG gọi hàm này — dùng `inviteBrandCreator`
 * (lời mời riêng M1 tự đóng đơn Pending đang chờ với lý do "Đã gửi lời mời riêng"). */
export const rejectBrandApplication = (applicationId: string, reason?: string) =>
  API.post<BrandApplicationForBusiness>(`${CAMPAIGNS_BASE}/applications/${applicationId}/reject`, {
    reason: reason || undefined,
  }).then((r) => r.data);

// ─────────── Creator ───────────

export interface BrandCreatorSetting {
  acceptIndividualOrders?: boolean; // mặc định bật
  acceptDeals: boolean;
  postPriceVnd?: number | null;
  liveMonthlyPriceVnd?: number | null;
  liveMonthlyHours?: number | null;
  liveHourlyPriceVnd?: number | null;
  taxCode?: string | null; // mã số thuế cá nhân đã lưu (không bắt buộc)
  updatedAt: string | null;
  // Chỉ GET trả: đã là creator chưa, đã eKYC chưa (trang chi tiết chiến dịch dùng để đặt đúng nút còn thiếu).
  isCreator?: boolean;
  isEkycVerified?: boolean;
}

export interface CreatorDealCampaignPreview {
  ordererKind?: OrdererKind;
  contentFormat?: BrandContentFormat | null;
  title: string;
  captionWithLink: string;
  images: MediaRef[];
  videos: MediaRef[];
  brandName: string;
  holdDays: number;
}

/** Brief của lời mời làm brief, creator gửi lại `briefVersion` khi bấm "Nhận làm brief". */
export interface CreatorDealBrief {
  briefText: string;
  briefVersion: number;
  submitDays: number;
  maxRevisions: number;
  /** Null = brief cũ (duyệt trong 48 giờ). */
  reviewDays: number | null;
  commitment: string;
  destinationUrl: string;
}

export interface CreatorDeal {
  id: string;
  campaign: CreatorDealCampaignPreview;
  priceVnd: number;
  netVnd: number;
  status: BrandDealStatus;
  respondBy: string;
  postId: string | null;
  endsAt: string | null;
  validClicks: number;

  // ── Nội dung theo từng lời mời (spec 2026-10-12 §5). Tuỳ chọn để FE chạy được khi payment chưa deploy. ──
  /** Gửi lại khi Đồng ý để máy chủ biết creator đang xem đúng bản mới nhất. */
  contentVersion?: number;
  existingPostId?: string | null;
  existingPostUnavailable?: boolean;
  directEditCount?: number;
  directEditMax?: number;
  canRequestEdit?: boolean;

  // ── Lời mời làm brief (spec 2026-10-12 §7). ──
  campaignKind?: 'Direct' | 'Creative';
  brief?: CreatorDealBrief | null;

  // ── Đo chuyển đổi (spec §2.3): clicks/conversions/conversionRate — KHÔNG orderId/value. ──
  stats: BrandDealStats;

  // ── M5: đánh giá 2 chiều — chỉ có ở chi tiết deal (spec §2.3). ──
  myRating?: BrandRatingSummary | null;
  theirRating?: BrandRatingSummary | null;
  ratingDueAt?: string | null;
  /** Phòng trao đổi của creator này trong chiến dịch (spec 2026-10-12 §3.1). */
  threadId?: string | null;
}

export interface AcceptBrandDealResult {
  id: string;
  status: BrandDealStatus;
  postId: string | null;
  endsAt: string | null;
  message: string | null;
}

const DEALS_BASE = '/v1/payment-service/brand-deals';

/** Khoảng giá deal/suất đang áp dụng — giá tối thiểu do admin cài trong Cài đặt hệ thống. */
export interface BrandDealLimits {
  minPriceVnd: number;
  maxPriceVnd: number;
}

export const getBrandDealLimits = () => API.get<BrandDealLimits>(`${DEALS_BASE}/limits`).then((r) => r.data);

export const getBrandCreatorSetting = () =>
  API.get<BrandCreatorSetting>(`${DEALS_BASE}/settings`).then((r) => r.data);

export interface BrandCreatorPrices {
  postPriceVnd: number | null;
  liveMonthlyPriceVnd: number | null;
  liveMonthlyHours: number | null;
  liveHourlyPriceVnd: number | null;
}

// Thay toàn bộ 4 giá; null = không bán kiểu đó.
export const setBrandCreatorPrices = (prices: BrandCreatorPrices) =>
  API.put<BrandCreatorSetting>(`${DEALS_BASE}/settings/prices`, prices).then((r) => r.data);

export const setBrandCreatorSetting = (acceptDeals: boolean) =>
  API.post<BrandCreatorSetting>(`${DEALS_BASE}/settings`, { acceptDeals }).then((r) => r.data);

// Chỉ gửi trường này: BE giữ nguyên acceptDeals khi request thiếu trường (ruling R15).
export const setBrandCreatorIndividualOrders = (accept: boolean) =>
  API.post<BrandCreatorSetting>(`${DEALS_BASE}/settings`, { acceptIndividualOrders: accept }).then((r) => r.data);

// Chỉ gửi trường này: BE giữ nguyên các cài đặt khác. Chuỗi rỗng = xoá mã số thuế.
export const setBrandCreatorTaxCode = (taxCode: string) =>
  API.post<BrandCreatorSetting>(`${DEALS_BASE}/settings`, { taxCode }).then((r) => r.data);

export const getMyBrandDeals = () => API.get<CreatorDeal[]>(DEALS_BASE).then((r) => r.data);

// ─────────── M5: đánh giá 2 chiều và điểm uy tín — creator (Task 3, spec §2.3) ───────────

/** 1 deal đã xong còn chờ chấm điểm — dùng chung creator/doanh nghiệp (shape giống nhau cả 2 route). */
export interface BrandRatingPending {
  ordererKind?: OrdererKind | null;
  dealId: string;
  campaignTitle: string;
  counterpartName: string;
  outcome: BrandDealStatus;
  ratingOpenedAt: string;
  dueAt: string;
}

export const getCreatorPendingRatings = () =>
  API.get<BrandRatingPending[]>(`${DEALS_BASE}/ratings/pending`).then((r) => r.data);

export interface SubmitBrandRatingResult {
  dealId: string;
  raterRole: 'Creator' | 'Business';
  stars: number;
  comment: string | null;
  createdAt: string;
  revealedAt: string | null;
}

export const submitCreatorRating = (dealId: string, stars: number, comment?: string) =>
  API.post<SubmitBrandRatingResult>(`${DEALS_BASE}/ratings/${dealId}`, { stars, comment: comment || undefined }).then(
    (r) => r.data
  );

/** Hồ sơ điểm công khai — chỉ lượt đã công bố, mới nhất trước, phân trang. Dùng chung cho dialog xem
 * nhận xét của 1 creator/doanh nghiệp (bấm vào sao mở ra, spec §3). */
export interface BrandReputationReview {
  stars: number;
  comment: string | null;
  createdAt: string;
}

export interface BrandReputation {
  ratingCount: number;
  average: number | null;
  isNew: boolean;
  isLowReputation: boolean;
  reviews: BrandReputationReview[];
}

export const getCreatorReputation = (userId: string, page = 1) =>
  API.get<BrandReputation>(`${DEALS_BASE}/ratings/creators/${userId}`, { params: { page } }).then((r) => r.data);

export const getBusinessReputation = (adAccountId: string, page = 1) =>
  API.get<BrandReputation>(`${DEALS_BASE}/ratings/businesses/${adAccountId}`, { params: { page } }).then(
    (r) => r.data
  );

export const declineBrandDeal = (dealId: string) =>
  API.post<BrandDealSummary>(`${DEALS_BASE}/${dealId}/decline`).then((r) => r.data);

/** `contentVersion` = bản nội dung creator đang xem; lệch bản mới nhất thì máy chủ trả 409. */
export const acceptBrandDeal = (dealId: string, contentVersion?: number) =>
  API.post<AcceptBrandDealResult>(`${DEALS_BASE}/${dealId}/accept`, undefined, {
    params: contentVersion ? { contentVersion } : undefined,
  }).then((r) => r.data);

// ─────────── Creator — sàn mở (M2) ───────────

/** 1 chiến dịch trong sàn mở — nhìn từ phía CREATOR (GET market). */
export interface BrandMarketListing {
  ordererKind?: OrdererKind;
  contentFormat?: BrandContentFormat | null;
  campaignId: string;
  brandName: string;
  title: string;
  /** Caption đã chèn chỗ đặt link tiếp thị — CHƯA phải link thật (chỉ có sau khi được duyệt). */
  captionWithLinkPreview: string;
  images: MediaRef[];
  videos: MediaRef[];
  holdDays: number;
  slotPriceVnd: number;
  netVnd: number;
  slotsLeft: number;
  slotsTotal: number;
  applyDeadline: string | null;
  minFollowers: number;
  categories: string[];
  eligible: boolean;
  ineligibleReason: string | null;
  /** Trạng thái đơn GẦN NHẤT của người xem trong chiến dịch này — null nếu chưa từng xin. */
  myApplicationStatus: BrandApplicationStatus | null;

  // ── M5: điểm uy tín doanh nghiệp đăng chiến dịch (spec §2.3). ──
  businessRatingAverage?: number | null;
  businessRatingCount?: number;
  businessLowReputation?: boolean;
  /** M5 (fix round 1): cần id này để mở hồ sơ điểm doanh nghiệp (`GET ratings/businesses/{id}`) khi
   * bấm vào sao trên thẻ sàn. */
  adAccountId?: string;
}

export const getBrandMarket = (params: {
  categoryId?: string;
  minNet?: number;
  sort?: 'newest' | 'price';
  format?: BrandContentFormat;
  page?: number;
}) => API.get<PaginationResponse<BrandMarketListing>>(`${DEALS_BASE}/market`, { params }).then((r) => r.data);

/** 1 đơn xin tham gia — nhìn từ phía CREATOR (apply/withdraw/GET applications). */
export interface BrandApplication {
  ordererKind?: OrdererKind;
  id: string;
  campaignId: string;
  campaignTitle: string;
  brandName: string;
  message: string | null;
  status: BrandApplicationStatus;
  rejectReason: string | null;
  dealId: string | null;
  createdAt: string;
  decidedAt: string | null;
  /** Phòng trao đổi của creator này trong chiến dịch (spec 2026-10-12 §3.1). */
  threadId?: string | null;
}

export const applyToBrandListing = (campaignId: string, message?: string) =>
  API.post<BrandApplication>(`${DEALS_BASE}/market/${campaignId}/apply`, { message: message || undefined }).then(
    (r) => r.data
  );

export const withdrawBrandApplication = (applicationId: string) =>
  API.post<BrandApplication>(`${DEALS_BASE}/applications/${applicationId}/withdraw`).then((r) => r.data);

export const getMyBrandApplications = () =>
  API.get<BrandApplication[]>(`${DEALS_BASE}/applications`).then((r) => r.data);

// ─────────── Creator — brief sáng tạo (M3, Task 6) ───────────
// Route theo payment branch feat/brand-creative: api/v1/brand-deals/briefs|tasks/* (cùng BASE với M1/M2).

/** 6 trạng thái MỚI của 1 nhiệm vụ (BrandDeal của brief Creative) + các trạng thái M1 dùng lại sau khi
 * bài được duyệt/đăng. */
export type BrandTaskStatus =
  | 'Working'
  | 'Submitted'
  | 'RevisionRequested'
  | 'Rejected'
  | 'Abandoned'
  | 'Overdue'
  | 'Compensated'
  | BrandDealStatus;

/** Mã lý do từ chối chuẩn (BrandDealConst) — nhãn tương ứng ở `REJECT_REASONS` (src/utils/brand-creative.ts). */
export type RejectReasonCode = 'OffBrief' | 'LowQuality' | 'NotOriginal' | 'PolicyViolation' | 'Other';

/** 1 brief trong sàn — nhìn từ phía CREATOR (GET brand-deals/briefs). */
export interface BrandBriefFeedItem {
  ordererKind?: OrdererKind;
  contentFormat?: BrandContentFormat | null;
  campaignId: string;
  brandName: string;
  title: string;
  briefText: string;
  submitDays: number;
  maxRevisions?: number;
  reviewDays?: number | null;
  /** Câu cam kết đầy đủ trước khi "Nhận deal" (payment dựng theo luật của brief). */
  claimCommitment?: string;
  images: MediaRef[];
  slotPriceVnd: number;
  netVnd: number;
  slotsLeft: number;
  slotsTotal: number;
  applyDeadline: string | null;
  minFollowers: number;
  categories: string[];
  eligible: boolean;
  ineligibleReason: string | null;

  // ── M5: điểm uy tín doanh nghiệp đăng brief (spec §2.3). ──
  businessRatingAverage?: number | null;
  businessRatingCount?: number;
  businessLowReputation?: boolean;
  /** M5 (fix round 1): cần id này để mở hồ sơ điểm doanh nghiệp (`GET ratings/businesses/{id}`) khi
   * bấm vào sao trên thẻ brief. */
  adAccountId?: string;
  /** "Creative" | "Proposal" — Proposal hiện nhãn "Nhận đề xuất" và nút "Gửi đề xuất". Thiếu = Creative (máy chủ cũ). */
  kind?: 'Creative' | 'Proposal';
  /** Trạng thái đề xuất gần nhất của người xem (chỉ Proposal). */
  myProposalStatus?: BrandProposalStatus | null;
}

/** 1 yêu cầu huỷ nhiệm vụ đang chờ admin quyết — có nghĩa job không tự duyệt bài nộp của nhiệm vụ này. */
export interface BrandCancelRequest {
  id: string;
  reason: string;
  status: 'Pending' | 'Approved' | 'Rejected';
  createdAt: string;
}

/** 1 nhiệm vụ (BrandDeal của brief Creative) — GET brand-deals/tasks, đủ cho "Việc của tôi" + bàn soạn bài. */
export interface BrandTask {
  ordererKind?: OrdererKind;
  contentFormat?: BrandContentFormat | null;
  dealId: string;
  campaignId: string;
  title: string;
  brandName: string;
  status: BrandTaskStatus;
  priceVnd: number;
  netVnd: number;
  submitDeadline: string | null;
  reviewDueAt: string | null;
  revisionCount: number;
  /** Số lần người đặt được yêu cầu sửa của brief (0–3). */
  maxRevisions?: number;
  /** Số ngày duyệt mỗi lần nộp; null = brief cũ (48 giờ). */
  reviewDays?: number | null;
  /** 0 = chưa nộp bài lần nào. */
  latestVersion: number;
  revisionNote: string | null;
  rejectReasonCode: RejectReasonCode | null;
  rejectReasonText: string | null;
  /** Chỉ có ở danh sách phía doanh nghiệp — null ở GET tasks phía creator. */
  creator: BrandCreatorCard | null;
  cancelRequest: BrandCancelRequest | null;
  /** Chỉ có khi Duyệt mà đăng bài chưa xong ngay (như M1). */
  message: string | null;
  /** M4: hạn kháng cáo (lúc từ chối + 14 ngày) — null nếu chưa bị từ chối hoặc từ chối trước bản này. */
  disputeWindowEndsAt: string | null;
  /** M4: trạng thái kháng cáo — chỉ có ở danh sách nhiệm vụ phía DOANH NGHIỆP, null ở danh sách creator. */
  disputeStatus: BrandDisputeStatus | null;
  /** Phòng trao đổi của creator này trong chiến dịch (spec 2026-10-12 §3.1). */
  threadId?: string | null;
  /** "Creative" | "Proposal" — thiếu = Creative (máy chủ cũ). */
  campaignKind?: 'Creative' | 'Proposal';
  /** Hạn creator bấm "Đăng công khai" (ISO có 'Z'), chỉ có khi AwaitingPublish. */
  publishDueAt?: string | null;
  autoPublished?: boolean;
}

export interface BrandSubmissionFile {
  fileId: string;
  kind: 'image' | 'video';
}

export interface BrandSubmissionVersion {
  submissionId: string;
  version: number;
  text: string;
  files: BrandSubmissionFile[];
  submittedAt: string;
}

/** Nội dung brief đang soạn bài theo — chỉ có ở GET tasks/{dealId} phía creator. */
export interface BrandTaskBrief {
  briefText: string;
  images: MediaRef[];
  destinationUrl: string;
  submitDays: number;
  /** Số bản brief của đơn; thiếu = 1. */
  version?: number;
}

export interface BrandDraft {
  text: string;
  fileIds: string[];
}

/** Chi tiết đầy đủ 1 nhiệm vụ nhìn từ phía CREATOR — brief/currentDraft luôn có giá trị, các trường
 * chỉ-doanh-nghiệp/admin (draftHistory/viewLog/events) không khai báo ở đây (FE creator không đọc). */
export interface BrandTaskDetail {
  task: BrandTask;
  versions: BrandSubmissionVersion[];
  brief: BrandTaskBrief | null;
  currentDraft: BrandDraft | null;
  /** M4: hạn kháng cáo (lúc từ chối + 14 ngày) — null nếu chưa bị từ chối hoặc từ chối trước bản này. */
  disputeWindowEndsAt: string | null;
  /** M4: kháng cáo của nhiệm vụ (cả ba phía) — null nếu chưa có. */
  dispute: BrandDispute | null;

  // ── Đo chuyển đổi (spec §2.3): clicks/conversions/conversionRate — chỉ có ở GET tasks/{dealId} phía
  // creator (GetTaskDetailHandler); null/undefined ở các DTO khác (business/admin). ──
  stats?: BrandDealStats | null;

  // ── M5: đánh giá 2 chiều (spec §2.3) — null ở phía ADMIN. ──
  myRating?: BrandRatingSummary | null;
  theirRating?: BrandRatingSummary | null;
  ratingDueAt?: string | null;
}

export interface BrandTicket {
  url: string;
  expiresAt: string;
  /** Fix round 1: backend trả đúng loại file (image|video) lấy từ BrandDraftFile/BrandSubmissionFile —
   * FE không còn cách nào khác để biết loại của 1 file nháp tải lại trang (gateway không có route HEAD). */
  kind: 'image' | 'video';
}

export const getBriefsFeed = (params: { categoryId?: string; sort?: 'newest' | 'price'; format?: BrandContentFormat; page?: number }) =>
  API.get<PaginationResponse<BrandBriefFeedItem>>(`${DEALS_BASE}/briefs`, { params }).then((r) => r.data);

export const claimBrief = (campaignId: string) =>
  API.post<BrandTask>(`${DEALS_BASE}/briefs/${campaignId}/claim`).then((r) => r.data);

export const getMyBrandTasks = () => API.get<BrandTask[]>(`${DEALS_BASE}/tasks`).then((r) => r.data);

export const abandonTask = (dealId: string) =>
  API.post<BrandTask>(`${DEALS_BASE}/tasks/${dealId}/abandon`).then((r) => r.data);

export const getTaskDetail = (dealId: string) =>
  API.get<BrandTaskDetail>(`${DEALS_BASE}/tasks/${dealId}`).then((r) => r.data);

export const saveDraft = (dealId: string, body: { text: string; fileIds: string[] }) =>
  API.put<BrandDraft>(`${DEALS_BASE}/tasks/${dealId}/draft`, body).then((r) => r.data);

export const getDraftFileTicket = (dealId: string, fileId: string) =>
  API.post<BrandTicket>(`${DEALS_BASE}/tasks/${dealId}/file-ticket`, { fileId }).then((r) => r.data);

export const submitTask = (dealId: string) =>
  API.post<BrandTask>(`${DEALS_BASE}/tasks/${dealId}/submit`).then((r) => r.data);

/** Creator kháng cáo bài bị từ chối — trong 14 ngày, mỗi nhiệm vụ một lần (spec §2.3). */
export const openDispute = (
  dealId: string,
  body: { description: string; evidenceLinks: string[]; evidenceImages: MediaRef[] }
) => API.post<BrandDispute>(`${DEALS_BASE}/tasks/${dealId}/dispute`, body).then((r) => r.data);

/** Vé xem (`BrandTicket.url`) là ĐƯỜNG DẪN TƯƠNG ĐỐI dạng `media-service/draft-files/preview/{id}?t=...`
 * — ghép với CÙNG API base + `/v1/` app dùng (giống `utils/public-tts.ts`) để ra URL tuyệt đối cho
 * `<img>`/`<video src>`. Gateway mở GET này KHÔNG xác thực, chính vé (?t=) là lớp kiểm duy nhất. */
export const buildDraftFileUrl = (ticketUrl: string): string => `${PUBLIC_API_ENDPOINT}/v1/${ticketUrl}`;

export interface UploadDraftFileResult {
  fileId: string;
  kind: 'image' | 'video';
}

/** Tải 1 file nháp lên media-service, có tiến độ (giống upload video/ảnh thường — `onUploadProgress`
 * của axios), lỗi trả 400 `{ message }` (dung lượng/định dạng/giới hạn số file). */
export const uploadDraftFile = (
  dealId: string,
  file: File,
  onProgress?: (percent: number) => void
) => {
  const fd = new FormData();
  fd.append('file', file);
  return API.post<UploadDraftFileResult>('/v1/media-service/draft-files/upload', fd, {
    params: { dealId },
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (evt) => {
      if (onProgress && evt.total) onProgress(Math.round((evt.loaded / evt.total) * 100));
    },
  }).then((r) => r.data);
};

// ─────────── Đơn nhận đề xuất — phía creator (spec 2026-10-12 §6) ───────────

export type BrandProposalStatus = 'Pending' | 'Accepted' | 'Declined' | 'Withdrawn' | 'Expired';

export interface BrandProposalFile {
  fileId: string;
  kind: 'image' | 'video';
}

/** Khoá tải file nháp của đề xuất (= id phòng trao đổi) — truyền vào `uploadDraftFile` thay cho dealId. */
export interface BrandProposalKey {
  uploadKey: string;
  threadId: string;
}

export interface BrandMyProposal {
  id: string;
  campaignId: string;
  campaignTitle: string;
  brandName: string;
  ordererKind?: OrdererKind;
  contentFormat?: BrandContentFormat | null;
  text: string;
  files: BrandProposalFile[];
  status: BrandProposalStatus;
  declineReason: string | null;
  expireReason: string | null;
  dealId: string | null;
  threadId: string;
  createdAt: string;
  decidedAt: string | null;
  /** Lần đầu người đặt mở đề xuất — có giá trị thì hiện "Người đặt đã xem". */
  viewedByOrdererAt: string | null;
}

export const getProposalUploadKey = (campaignId: string) =>
  API.post<BrandProposalKey>(`${DEALS_BASE}/market/${campaignId}/proposal-key`).then((r) => r.data);

export const sendBrandProposal = (campaignId: string, body: { text: string; fileIds: string[] }) =>
  API.post<BrandMyProposal>(`${DEALS_BASE}/market/${campaignId}/proposals`, body).then((r) => r.data);

export const getMyBrandProposals = () => API.get<BrandMyProposal[]>(`${DEALS_BASE}/proposals`).then((r) => r.data);

export const withdrawBrandProposal = (proposalId: string) =>
  API.post<BrandMyProposal>(`${DEALS_BASE}/proposals/${proposalId}/withdraw`).then((r) => r.data);

/** Vé xem bản gốc một file trong đề xuất của chính mình — `url` ghép bằng `buildDraftFileUrl`. */
export const getProposalFileTicket = (proposalId: string, fileId: string) =>
  API.post<BrandTicket>(`${DEALS_BASE}/proposals/${proposalId}/file-ticket`, { fileId }).then((r) => r.data);

/** "Đăng công khai" bài đã duyệt của đơn nhận đề xuất. */
export const publishProposalTask = (dealId: string) =>
  API.post<BrandTask>(`${DEALS_BASE}/tasks/${dealId}/publish`).then((r) => r.data);


// ─────────── Doanh nghiệp — brief sáng tạo: nhiệm vụ + duyệt bài (M3, Task 7) ───────────
// Cùng BASE với M1/M2 (api/v1/brand-campaigns/*).

/** 1 dòng lịch sử quyết định nhiệm vụ — Ruling R7 (Submitted/Approved/RevisionRequested/Rejected/
 * CancelRequested/CancelApproved/CancelRejected/Overdue/Abandoned/AutoApproved…), nhãn ở `taskEventLabel`
 * (src/utils/brand-creative.ts). */
export interface BrandTaskEvent {
  kind: string;
  note: string | null;
  createdAt: string;
}

/** 1 dòng lịch sử soạn thảo — chỉ ADMIN xem được (bằng chứng cho M4). */
export interface BrandDraftSnapshot {
  text: string;
  fileIds: string[];
  createdAt: string;
}

/** 1 dòng nhật ký xem — chỉ ADMIN xem được (bằng chứng cho M4). */
export interface BrandPreviewViewLog {
  viewerUserId: string;
  viewerRole: 'Business' | 'Admin';
  fileId: string | null;
  createdAt: string;
}

/** Chi tiết đầy đủ 1 nhiệm vụ nhìn từ phía DOANH NGHIỆP/ADMIN — `draftHistory`/`viewLog` chỉ có ở admin. */
export interface BrandTaskFullDetail {
  task: BrandTask;
  versions: BrandSubmissionVersion[];
  draftHistory: BrandDraftSnapshot[] | null;
  viewLog: BrandPreviewViewLog[] | null;
  events: BrandTaskEvent[] | null;
  /** M4: hạn kháng cáo (lúc từ chối + 14 ngày) — null nếu chưa bị từ chối hoặc từ chối trước bản này. */
  disputeWindowEndsAt: string | null;
  /** M4: kháng cáo của nhiệm vụ (cả ba phía) — null nếu chưa có. */
  dispute: BrandDispute | null;

  // ── Lời mời làm brief: chỉ có khi đơn là lời mời làm brief (người đặt, admin). ──
  brief?: BrandTaskBrief | null;
  briefEditedAfterReview?: boolean;

  // ── M5: đánh giá 2 chiều (spec §2.3) — null ở phía ADMIN. ──
  myRating?: BrandRatingSummary | null;
  theirRating?: BrandRatingSummary | null;
  ratingDueAt?: string | null;
}

/** Nhiệm vụ của 1 brief — nhìn từ phía DOANH NGHIỆP (chủ brief), mỗi phần tử kèm thẻ creator. */
export const getBrandTasksForBusiness = (campaignId: string) =>
  API.get<BrandTask[]>(`${CAMPAIGNS_BASE}/${campaignId}/tasks`).then((r) => r.data);

export const getBusinessTaskDetail = (dealId: string) =>
  API.get<BrandTaskFullDetail>(`${CAMPAIGNS_BASE}/tasks/${dealId}`).then((r) => r.data);

/** Vé xem bản ĐÃ ĐÓNG DẤU của 1 file đã nộp — `url` ghép tuyệt đối bằng `buildDraftFileUrl` (cùng helper Task 6). */
export const getBusinessPreviewTicket = (dealId: string, fileId: string) =>
  API.post<BrandTicket>(`${CAMPAIGNS_BASE}/tasks/${dealId}/preview-ticket`, { fileId }).then((r) => r.data);

export const markSubmissionViewed = (dealId: string, submissionId: string) =>
  API.post<{ ok: boolean }>(`${CAMPAIGNS_BASE}/tasks/${dealId}/viewed`, { submissionId }).then((r) => r.data);

export const approveTask = (dealId: string) =>
  API.post<BrandTask>(`${CAMPAIGNS_BASE}/tasks/${dealId}/approve`).then((r) => r.data);

export const requestTaskRevision = (dealId: string, note: string) =>
  API.post<BrandTask>(`${CAMPAIGNS_BASE}/tasks/${dealId}/request-revision`, { note }).then((r) => r.data);

export const rejectTask = (dealId: string, reasonCode: string, reasonText?: string) =>
  API.post<BrandTask>(`${CAMPAIGNS_BASE}/tasks/${dealId}/reject`, { reasonCode, reasonText: reasonText || undefined }).then(
    (r) => r.data
  );

export const requestTaskCancel = (dealId: string, reason: string) =>
  API.post<BrandCancelRequest>(`${CAMPAIGNS_BASE}/tasks/${dealId}/cancel-request`, { reason }).then((r) => r.data);

/** Doanh nghiệp (chủ brief) giải trình kháng cáo — một lần, trong 3 ngày (spec §2.3). */
export const respondDispute = (dealId: string, body: { response: string; links: string[] }) =>
  API.post<BrandDispute>(`${CAMPAIGNS_BASE}/tasks/${dealId}/dispute/respond`, body).then((r) => r.data);

// ─────────── Đơn nhận đề xuất — phía người đặt (spec 2026-10-12 §6) ───────────

export interface BrandProposalForBusiness {
  id: string;
  creator: BrandCreatorCard;
  text: string;
  files: BrandProposalFile[];
  status: BrandProposalStatus;
  declineReason: string | null;
  expireReason: string | null;
  dealId: string | null;
  threadId: string;
  createdAt: string;
  decidedAt: string | null;
}

export interface AcceptBrandProposalResult {
  proposalId: string;
  dealId: string;
  status: BrandProposalStatus;
  dealStatus: BrandTaskStatus;
  reviewDueAt: string | null;
}

export const getCampaignProposals = (campaignId: string, status?: BrandProposalStatus) =>
  API.get<BrandProposalForBusiness[]>(`${CAMPAIGNS_BASE}/${campaignId}/proposals`, { params: status ? { status } : {} }).then(
    (r) => r.data
  );

/** Vé xem bản ĐÃ ĐÓNG DẤU một file của đề xuất đang chờ — mỗi lần xin vé là một dòng nhật ký xem. */
export const getProposalPreviewTicket = (proposalId: string, fileId: string) =>
  API.post<BrandTicket>(`${CAMPAIGNS_BASE}/proposals/${proposalId}/preview-ticket`, { fileId }).then((r) => r.data);

export const markProposalViewed = (proposalId: string) =>
  API.post<{ ok: boolean }>(`${CAMPAIGNS_BASE}/proposals/${proposalId}/viewed`).then((r) => r.data);

export const acceptBrandProposal = (proposalId: string) =>
  API.post<AcceptBrandProposalResult>(`${CAMPAIGNS_BASE}/proposals/${proposalId}/accept`).then((r) => r.data);

export const declineBrandProposal = (proposalId: string, reason?: string) =>
  API.post<BrandProposalForBusiness>(`${CAMPAIGNS_BASE}/proposals/${proposalId}/decline`, { reason: reason || undefined }).then(
    (r) => r.data
  );


// ─────────── Admin ───────────

export interface AdminBrandDeal {
  ordererKind?: OrdererKind; ownerIdentityName?: string | null;
  id: string;
  campaignId: string;
  creatorId: string;
  priceVnd: number;
  netVnd: number;
  status: BrandDealStatus;
  postId: string | null;
  endsAt: string | null;
  campaignTitle: string;
  brandName: string;
  /** M-9: lý do chuyển NeedsReview (vd "Bài bị khoá"/"Bài bị ẩn do báo cáo") — null nếu job chưa set (deal cũ trước fix). */
  closeReason: string | null;
  /** Nội dung đơn đã đổi so với bản lúc mời (bản mới nhất > 1). Tuỳ chọn để FE chạy được khi payment chưa deploy. */
  editedAfterReview?: boolean;
}

const ADMIN_BASE = '/v1/payment-service/admin/brand-deals';

export const adminListBrandCampaigns = (status: string = 'PendingReview') =>
  API.get<BrandCampaign[]>(`${ADMIN_BASE}/campaigns`, { params: { status } }).then((r) => r.data);

export const adminReviewBrandCampaign = (campaignId: string, approve: boolean, reason?: string) =>
  API.post<BrandCampaign>(`${ADMIN_BASE}/campaigns/${campaignId}/review`, { approve, reason }).then(
    (r) => r.data
  );

export const adminListBrandDeals = (status: string = 'NeedsReview') =>
  API.get<AdminBrandDeal[]>(`${ADMIN_BASE}/deals`, { params: { status } }).then((r) => r.data);

export const adminResolveBrandDeal = (
  dealId: string,
  totpCode: string,
  payCreator: boolean,
  note?: string
) =>
  API.post<AdminBrandDeal>(
    `${ADMIN_BASE}/deals/${dealId}/resolve`,
    { payCreator, note },
    { params: { totpCode } }
  ).then((r) => r.data);

// ─────────── Admin — brief sáng tạo: nhiệm vụ + yêu cầu huỷ (M3, Task 7) ───────────

/** 1 yêu cầu huỷ nhiệm vụ — nhìn từ phía ADMIN (GET cancel-requests, mặc định status=Pending). */
export interface AdminBrandCancelRequest {
  ordererKind?: OrdererKind; ownerIdentityName?: string | null;
  id: string;
  dealId: string;
  campaignId: string;
  campaignTitle: string;
  brandName: string;
  creatorId: string;
  dealStatus: BrandTaskStatus;
  priceVnd: number;
  reviewDueAt: string | null;
  reason: string;
  status: 'Pending' | 'Approved' | 'Rejected';
  createdAt: string;
  decidedAt: string | null;
  decisionNote: string | null;
}

export const getAdminTaskDetail = (dealId: string) =>
  API.get<BrandTaskFullDetail>(`${ADMIN_BASE}/tasks/${dealId}`).then((r) => r.data);

/** Admin xin vé xem bản ĐÃ ĐÓNG DẤU của 1 file đã nộp — ghi nhật ký xem `ViewerRole = Admin`. */
export const getAdminPreviewTicket = (dealId: string, fileId: string) =>
  API.post<BrandTicket>(`${ADMIN_BASE}/tasks/${dealId}/preview-ticket`, { fileId }).then((r) => r.data);

export const adminListCancelRequests = (status: string = 'Pending') =>
  API.get<AdminBrandCancelRequest[]>(`${ADMIN_BASE}/cancel-requests`, { params: { status } }).then((r) => r.data);

export const adminApproveCancelRequest = (requestId: string, totpCode: string) =>
  API.post<AdminBrandCancelRequest>(
    `${ADMIN_BASE}/cancel-requests/${requestId}/approve`,
    {},
    { params: { totpCode } }
  ).then((r) => r.data);

export const adminRejectCancelRequest = (requestId: string, totpCode: string, note?: string) =>
  API.post<AdminBrandCancelRequest>(
    `${ADMIN_BASE}/cancel-requests/${requestId}/reject`,
    { note: note || undefined },
    { params: { totpCode } }
  ).then((r) => r.data);

// ─────────── Admin — kháng cáo (M4, Task 4) ───────────
// `AdminGetDisputeRequest`/`AdminListDisputesRequest` phía payment (BrandDisputeHandlers.cs).

/** 1 dòng danh sách kháng cáo — nhìn từ phía ADMIN (GET disputes, mặc định status=Open, cũ nhất trước). */
export interface AdminBrandDisputeListItem {
  ordererKind?: OrdererKind; ownerIdentityName?: string | null;
  dispute: BrandDispute;
  campaignId: string;
  title: string;
  adAccountId: string;
  businessName: string;
  creatorId: string;
  priceVnd: number;
}

/** Chi tiết 1 kháng cáo — kèm bằng chứng M3 (`task`, CÙNG shape với `BrandTaskFullDetail` — cùng DTO
 * `BrandTaskDetailDto` phía payment mà `getAdminTaskDetail` đã dùng). */
export interface AdminBrandDisputeDetail {
  ordererKind?: OrdererKind; ownerIdentityName?: string | null;
  dispute: BrandDispute;
  adAccountId: string;
  businessName: string;
  /** Dữ liệu xem trước tiền hai bên khi chọn tỉ lệ. */
  priceVnd?: number;
  feePercent?: number;
  vndPerCa?: number;
  task: BrandTaskFullDetail;
}

export const adminListDisputes = (status: string = 'Open') =>
  API.get<AdminBrandDisputeListItem[]>(`${ADMIN_BASE}/disputes`, { params: { status } }).then((r) => r.data);

export const adminGetDispute = (disputeId: string) =>
  API.get<AdminBrandDisputeDetail>(`${ADMIN_BASE}/disputes/${disputeId}`).then((r) => r.data);

/** Quyết kháng cáo theo tỉ lệ creator nhận 0–100%, bước 10% (100 = creator đúng, 0 = không đủ căn cứ, giữa = chia tiền) —
 * bắt buộc lý do 10–1.000 ký tự + mã Authenticator. */
export const adminDecideDispute = (disputeId: string, totpCode: string, creatorPercent: number, reason: string) =>
  API.post<BrandDispute>(
    `${ADMIN_BASE}/disputes/${disputeId}/decide`,
    decideDisputeBody(creatorPercent, reason),
    { params: { totpCode } }
  ).then((r) => r.data);

/** Gửi kèm `upheld` cho mức 100%/0% để bản payment cũ (chưa có chia tiền) vẫn quyết được nếu phải quay lại bản trước;
 * bản mới ưu tiên `creatorPercent` khi có cả hai. */
export const decideDisputeBody = (creatorPercent: number, reason: string) => ({
  creatorPercent,
  reason,
  upheld: creatorPercent === 100 ? true : creatorPercent === 0 ? false : undefined,
});

// ─────────── Admin — doanh nghiệp bị khoá khỏi sàn (M4, Task 4, chỉ SuperAdmin) ───────────
// Cùng route `admin/brand-deals/*` nhưng xử lý ở `AdminBrandMarketplaceController` (kiểm role riêng,
// KHÔNG chung `RequirePermission("Ads.Manage")` của `AdminBrandDealController`).

/** 1 tài khoản doanh nghiệp đang bị khoá khỏi sàn — nhìn từ phía ADMIN (GET banned-accounts). */
export interface AdminBannedAccount {
  ordererKind?: OrdererKind; ownerIdentityName?: string | null;
  id: string;
  businessName: string;
  ownerUserId: string;
  marketplaceBannedAt: string;
  marketplaceBanReason: string | null;
  marketplaceBannedBy: string | null;
  /** Kháng cáo đã dẫn tới khoá — null nếu là khoá thủ công (không qua quyết kháng cáo). */
  marketplaceBanDisputeId: string | null;
}

export const adminListBannedAccounts = () =>
  API.get<AdminBannedAccount[]>(`${ADMIN_BASE}/banned-accounts`).then((r) => r.data);

/** Khoá tay 1 tài khoản (vd khoá tự động lỗi giữa chừng sau quyết kháng cáo) — bắt buộc lý do + mã Authenticator. */
export const adminBanAccount = (adAccountId: string, totpCode: string, reason: string) =>
  API.post<AdAccount>(`${ADMIN_BASE}/accounts/${adAccountId}/ban`, { reason }, { params: { totpCode } }).then(
    (r) => r.data
  );

export const adminUnbanAccount = (adAccountId: string, totpCode: string) =>
  API.post<AdAccount>(`${ADMIN_BASE}/accounts/${adAccountId}/unban`, {}, { params: { totpCode } }).then(
    (r) => r.data
  );

// ─────────── Admin — điểm uy tín (M5, Task 4, spec §2.3) ───────────
// `AdminListReputationsRequest`/`AdminGetReputationRequest`/`AdminSetReputationExemptRequest` phía
// payment (BrandReputationAdminHandlers.cs). `subjectType` chỉ nhận 'Creator' | 'Business'.

export type BrandRatingSubjectType = 'Creator' | 'Business';

/** 1 hồ sơ điểm uy tín — nhìn từ phía ADMIN (GET reputations). `subjectName` (Task 4 fix review): tên
 * doanh nghiệp/creator khi BE giải quyết được; rỗng nếu subject không còn tồn tại — FE rơi về id rút gọn. */
export interface AdminReputation {
  subjectType: BrandRatingSubjectType;
  subjectId: string;
  subjectName: string;
  ratingCount: number;
  average: number | null;
  isLowReputation: boolean;
  exempt: boolean;
  exemptReason: string | null;
  exemptBy: string | null;
  exemptAt: string | null;
}

/** 1 lượt chấm — kể cả CHƯA công bố (chỉ admin xem được, khác hồ sơ điểm công khai). */
export interface AdminRatingRow {
  id: string;
  dealId: string;
  raterRole: 'Creator' | 'Business';
  stars: number;
  comment: string | null;
  createdAt: string;
  revealedAt: string | null;
  /** Có giá trị: admin đã ẩn, không hiện công khai và không tính điểm. */
  hiddenAt: string | null;
  hiddenReason: string | null;
}

export interface AdminReputationDetail extends AdminReputation {
  ratings: AdminRatingRow[];
}

export const adminListReputations = (lowOnly: boolean = false) =>
  API.get<AdminReputation[]>(`${ADMIN_BASE}/reputations`, { params: { lowOnly } }).then((r) => r.data);

export const adminGetReputation = (subjectType: BrandRatingSubjectType, subjectId: string) =>
  API.get<AdminReputationDetail>(`${ADMIN_BASE}/reputations/${subjectType}/${subjectId}`).then((r) => r.data);

/** Đặt/bỏ miễn trừ — bắt buộc lý do 10–500 ký tự + mã Authenticator. */
export const adminSetReputationExempt = (
  subjectType: BrandRatingSubjectType,
  subjectId: string,
  totpCode: string,
  exempt: boolean,
  reason: string
) =>
  API.post<AdminReputation>(
    `${ADMIN_BASE}/reputations/${subjectType}/${subjectId}/exempt`,
    { exempt, reason },
    { params: { totpCode } }
  ).then((r) => r.data);

// ─────────── Admin — đo chuyển đổi: danh sách + huỷ (spec §2.3) ───────────

/** 1 chuyển đổi nhìn từ phía ADMIN (kể cả đã huỷ/thử) — có đủ orderId/value (khác DTO creator). */
export interface AdminBrandConversion {
  id: string;
  campaignId: string;
  dealId: string;
  creatorId: string;
  orderId: string;
  valueVnd: number | null;
  source: string;
  isTest: boolean;
  createdAt: string;
  voidedAt: string | null;
  voidedBy: string | null;
  voidReason: string | null;
}

/** Danh sách chuyển đổi của 1 chiến dịch (kể cả đã huỷ), mới nhất trước, phân trang. */
export const adminListBrandConversions = (campaignId: string, page: number = 1) =>
  API.get<PaginationResponse<AdminBrandConversion>>(`${ADMIN_BASE}/campaigns/${campaignId}/conversions`, {
    params: { page },
  }).then((r) => r.data);

/** Huỷ 1 chuyển đổi (gian lận) — bắt buộc lý do 10–500 ký tự + mã Authenticator; bị loại khỏi báo cáo
 * ngay sau khi huỷ. */
export const adminVoidBrandConversion = (conversionId: string, totpCode: string, reason: string) =>
  API.post<AdminBrandConversion>(
    `${ADMIN_BASE}/conversions/${conversionId}/void`,
    { reason },
    { params: { totpCode } }
  ).then((r) => r.data);

// ─────────── Nội dung theo từng lời mời, đề nghị chỉnh, bài có sẵn (spec 2026-10-12 §5) ───────────

export interface BrandDealContent {
  dealId: string;
  version: number;
  caption: string;
  images: MediaRef[];
  videos: MediaRef[];
  existingPostId: string | null;
  existingPostUnavailable: boolean;
  createdAt: string | null;
}

/** Nội dung mới: caption/images/videos; bài có sẵn: chỉ existingPostId. */
export interface SaveBrandDealContentBody {
  caption?: string;
  images?: MediaRef[];
  videos?: MediaRef[];
  existingPostId?: string | null;
}

export interface BrandSponsorCandidate {
  postId: string;
  content: string;
  images: MediaRef[];
  videos: MediaRef[];
  createdAt: string;
}

export interface BrandSponsorCandidatePage {
  items: BrandSponsorCandidate[];
  hasMore: boolean;
}

export interface BrandDealEditRequestResult {
  directEditCount: number;
  directEditMax: number;
}

export const requestBrandDealEdit = (dealId: string, note: string) =>
  API.post<BrandDealEditRequestResult>(`${DEALS_BASE}/${dealId}/edit-requests`, { note }).then((r) => r.data);

export const getBrandDealContent = (dealId: string) =>
  API.get<BrandDealContent>(`${CAMPAIGNS_BASE}/deals/${dealId}/content`).then((r) => r.data);

export const updateBrandDealContent = (dealId: string, body: SaveBrandDealContentBody) =>
  API.put<BrandDealContent>(`${CAMPAIGNS_BASE}/deals/${dealId}/content`, body).then((r) => r.data);

export const getCreatorSponsorCandidates = (campaignId: string, creatorId: string, page = 1) =>
  API.get<BrandSponsorCandidatePage>(`${CAMPAIGNS_BASE}/${campaignId}/creators/${creatorId}/posts`, { params: { page } }).then(
    (r) => r.data
  );

// ─────────── Lời mời làm brief (spec 2026-10-12 §7) ───────────

export interface BrandDealBrief {
  dealId: string;
  briefText: string;
  briefVersion: number;
  respondBy: string;
}

/** Người đặt lưu brief mới cho lời mời làm brief còn chờ; máy chủ tăng bản và nới hạn trả lời. */
export const updateBrandDealBrief = (dealId: string, briefText: string) =>
  API.put<BrandDealBrief>(`${CAMPAIGNS_BASE}/deals/${dealId}/brief`, { briefText }).then((r) => r.data);

/** `briefVersion` = bản brief creator đang xem; lệch bản hiện tại thì máy chủ trả 409. */
export const acceptBriefInvite = (dealId: string, briefVersion?: number) =>
  API.post<BrandTask>(`${DEALS_BASE}/${dealId}/accept-brief`, undefined, {
    params: briefVersion ? { briefVersion } : undefined,
  }).then((r) => r.data);
